/**
 * ECOBAG GIFT — MISSÃO BURST
 * Burst Suplementos — Tema Impact
 *
 * Regra:
 *  - Se o carrinho tiver QUALQUER variante do Super Café com Creatina (MISSÃO BURST)
 *    E o subtotal (sem contar a Ecobag) for >= R$ 190,00
 *    → adiciona 1 Ecobag Burst automaticamente
 *  - Se o gatilho for removido OU o subtotal cair abaixo de R$ 190
 *    → remove a Ecobag automaticamente
 *  - Se o cliente tentar alterar a quantidade da Ecobag brinde
 *    → força de volta para 1
 *
 * O preço da Ecobag é zerado no checkout pela regra nativa "Compre X Leve Y"
 * do Shopify (MISSÃO BURST, 15/abr–30/abr).
 */

(function () {
  'use strict';

  const CONFIG = {
    // Variantes gatilho: qualquer uma aciona a Ecobag
    triggerVariantIds: [44369086447709, 44369086480477, 44369086414941],
    // Variante da Ecobag brinde
    ecobagVariantId: 43167982780509,
    // Valor mínimo do carrinho em centavos (R$ 190,00)
    minCartValue: 19000,
    // Quantidade fixa de Ecobag
    ecobagQuantity: 1,
    // Property que marca o line item como brinde
    giftPropertyKey: '_gift',
    giftPropertyValue: 'MISSÃO BURST',
  };

  let syncInProgress = false;
  let lastSyncTime = 0;
  const SYNC_DEBOUNCE_MS = 250;

  // ========== API do carrinho ==========

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Falha ao carregar /cart.js');
    return res.json();
  }

  async function addEcobag() {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: CONFIG.ecobagVariantId,
            quantity: CONFIG.ecobagQuantity,
            properties: {
              [CONFIG.giftPropertyKey]: CONFIG.giftPropertyValue,
            },
          },
        ],
      }),
    });
  }

  async function changeEcobagByLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ line, quantity }),
    });
  }

  // ========== Lógica ==========

  function isGiftItem(item) {
    return (
      item.variant_id === CONFIG.ecobagVariantId &&
      item.properties &&
      item.properties[CONFIG.giftPropertyKey] === CONFIG.giftPropertyValue
    );
  }

  function findGiftLineIndex(cart) {
    const idx = cart.items.findIndex(isGiftItem);
    return idx >= 0 ? idx + 1 : -1; // /cart/change usa line 1-indexed
  }

  function findGiftItem(cart) {
    return cart.items.find(isGiftItem);
  }

  function hasTriggerProduct(cart) {
    return cart.items.some(
      (item) =>
        CONFIG.triggerVariantIds.includes(item.variant_id) && !isGiftItem(item)
    );
  }

  function subtotalWithoutGift(cart) {
    return cart.items.reduce((sum, item) => {
      if (isGiftItem(item)) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function shouldHaveEcobag(cart) {
    return (
      hasTriggerProduct(cart) && subtotalWithoutGift(cart) >= CONFIG.minCartValue
    );
  }

  // ========== Sincronização ==========

  async function syncEcobag() {
    const now = Date.now();
    if (syncInProgress) return;
    if (now - lastSyncTime < SYNC_DEBOUNCE_MS) return;

    syncInProgress = true;
    lastSyncTime = now;

    try {
      const cart = await getCart();
      const shouldHave = shouldHaveEcobag(cart);
      const giftItem = findGiftItem(cart);
      const giftLine = findGiftLineIndex(cart);

      let changed = false;

      if (shouldHave && !giftItem) {
        // Adicionar Ecobag
        await addEcobag();
        changed = true;
      } else if (shouldHave && giftItem && giftItem.quantity !== CONFIG.ecobagQuantity) {
        // Cliente alterou quantidade — forçar de volta pra 1
        await changeEcobagByLine(giftLine, CONFIG.ecobagQuantity);
        changed = true;
      } else if (!shouldHave && giftItem) {
        // Remover Ecobag
        await changeEcobagByLine(giftLine, 0);
        changed = true;
      }

      if (changed) refreshCartUI();
    } catch (err) {
      console.error('[Ecobag Gift] Erro ao sincronizar:', err);
    } finally {
      syncInProgress = false;
    }
  }

  function refreshCartUI() {
    // Dispara eventos que diferentes temas escutam pra atualizar a UI
    const events = [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'theme:cart:change',
      'theme:cart:update',
    ];
    events.forEach((name) => {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      } catch (e) {}
    });

    // Impact / Maestrooo costuma expor métodos via window.theme
    try {
      if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
        window.theme.cart.refresh();
      }
    } catch (e) {}
  }

  // ========== Interceptadores ==========

  // Intercepta fetch() para detectar mutações no carrinho feitas pelo tema
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const arg0 = args[0];
    const url =
      typeof arg0 === 'string'
        ? arg0
        : arg0 && typeof arg0.url === 'string'
        ? arg0.url
        : '';
    const isCartMutation = /\/cart\/(add|update|change|clear)(\.js|\?|$)/.test(url);

    return originalFetch.apply(this, args).then((response) => {
      if (isCartMutation && response && response.ok) {
        // Pequeno delay pra garantir que o cart já foi persistido
        setTimeout(() => syncEcobag(), 120);
      }
      return response;
    });
  };

  // Intercepta XMLHttpRequest (alguns temas antigos ou plugins ainda usam)
  const OriginalXHR = window.XMLHttpRequest;
  function PatchedXHR() {
    const xhr = new OriginalXHR();
    const originalOpen = xhr.open;
    let requestUrl = '';
    xhr.open = function (method, url, ...rest) {
      requestUrl = url || '';
      return originalOpen.call(xhr, method, url, ...rest);
    };
    xhr.addEventListener('load', function () {
      if (
        /\/cart\/(add|update|change|clear)(\.js|\?|$)/.test(requestUrl) &&
        xhr.status >= 200 &&
        xhr.status < 300
      ) {
        setTimeout(() => syncEcobag(), 120);
      }
    });
    return xhr;
  }
  // Preserva propriedades estáticas do XMLHttpRequest
  PatchedXHR.prototype = OriginalXHR.prototype;
  Object.keys(OriginalXHR).forEach((k) => {
    try {
      PatchedXHR[k] = OriginalXHR[k];
    } catch (e) {}
  });
  window.XMLHttpRequest = PatchedXHR;

  // Sincroniza no load inicial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncEcobag());
  } else {
    syncEcobag();
  }

  // Re-sincroniza quando o usuário volta pra aba (caso tenha mexido em outra)
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') syncEcobag();
  });

  console.log('[Ecobag Gift] MISSÃO BURST — script carregado');
})();
